import * as Sentry from '@sentry/react';
import { call, put, select } from 'redux-saga/effects';
import {
  archive,
  getOne,
  permanentlyDeleteArchived,
  removeOne,
  restore,
} from 'api/cloudsave/cloudsaveApi';
import { AuthenticationError, NetworkError, TimeoutError } from 'api/apiClient';
import keycloak from 'auth/keycloak';
import {
  actionUndone,
  allArchivedDeletedMessage,
  errorMessage,
  fileArchivedMessage,
  fileRestoredMessage,
  storageUnitDeleted,
} from 'containers/Snackbar/snackbarMessages';
import { getIsOnline } from 'store/connection/selectors';
import type { RootState } from 'store';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import { requestConnectivityCheck } from 'util/connectivityChecks';
import {
  archiveStorageUnitSuccess,
  deleteAllArchviedSuccess,
  removeStorageUnitSuccess,
  restoreStorageUnitSuccess,
  setExplorerLoading,
} from './reducer';
import type {
  ArchiveStorageUnitRequestActionInterface,
  RemoveStorageUnitRequestActionInterface,
  RestoreStorageUnitRequestActionInterface,
} from './sagaActions';
import type { StorageUnit, StorageUnitsReducerInterface } from './types';
import { mergeSyncedStorageUnit } from './utils';
import {
  clearCachedStorageUnitTree,
  findStorageUnitInState,
} from './storageUnitStateUtils';

export function* removeStorageUnit(
  action: RemoveStorageUnitRequestActionInterface,
) {
  const { id } = action.payload;
  const storageUnitsState: StorageUnitsReducerInterface = yield select(
    (state: RootState) => state.storageUnitsState,
  );
  const oldUnit = findStorageUnitInState(storageUnitsState, id);
  try {
    yield put(setExplorerLoading(true));
    const removedLocalCreate: boolean = yield* removeCreatedStorageUnitLocally(
      id,
      oldUnit,
    );
    if (removedLocalCreate) {
      return;
    }

    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline || !keycloak.token) {
      yield put(
        addMessage(
          errorMessage(
            `Couldn't remove ${oldUnit?.type}. Please try again later.`,
          ),
        ),
      );
      return;
    }

    yield withRetry(() => removeOne(id));

    yield storageUnitCache.clearOne(id);
    yield storageUnitCache.clearList();

    yield put(removeStorageUnitSuccess(id));

    if (oldUnit) {
      yield put(addMessage(storageUnitDeleted(oldUnit)));
    }
  } catch (err) {
    if (
      err instanceof NetworkError ||
      err instanceof TimeoutError ||
      err instanceof AuthenticationError
    ) {
      if (!(err instanceof AuthenticationError))
        yield call(requestConnectivityCheck);
      const removedLocalCreate: boolean =
        yield* removeCreatedStorageUnitLocally(id, oldUnit);
      if (removedLocalCreate) {
        return;
      }
    }

    Sentry.captureException(err, {
      tags: { saga: 'removeStorageUnit' },
      extra: { id, unitType: oldUnit?.type },
    });
    yield put(
      addMessage(
        errorMessage(
          `Couldn't remove ${oldUnit?.type}. Please try again later.`,
        ),
      ),
    );
  } finally {
    yield put(setExplorerLoading(false));
  }
}

function* removeCreatedStorageUnitLocally(
  id: string,
  unitFromState?: StorageUnit,
): Generator<unknown, boolean, unknown> {
  let unitToRemove = unitFromState;
  unitToRemove ??= (yield call(
    [storageUnitCache, storageUnitCache.getOne],
    id,
  )) as StorageUnit | undefined;

  if (unitToRemove?.pendingAction !== 'create' && !id.startsWith('tempId-')) {
    return false;
  }

  yield* clearCachedStorageUnitTree(id);
  yield storageUnitCache.removeUnitFromLists(id);
  yield put(removeStorageUnitSuccess(id));
  yield put(addMessage(actionUndone()));
  return true;
}

function* archiveStorageUnitOffline(
  id: string,
  unitFromState?: StorageUnit,
): Generator<unknown, void, unknown> {
  const cachedUnit = (yield call(
    [storageUnitCache, storageUnitCache.getOne],
    id,
  )) as StorageUnit | null;
  const unitToArchive = unitFromState
    ? {
        ...(cachedUnit ?? {}),
        ...unitFromState,
        fileData: unitFromState.fileData ?? cachedUnit?.fileData,
        thumbnail: unitFromState.thumbnail ?? cachedUnit?.thumbnail,
        isSynced: unitFromState.isSynced ?? cachedUnit?.isSynced,
        syncedAt: unitFromState.syncedAt ?? cachedUnit?.syncedAt,
        localUpdatedAt:
          unitFromState.localUpdatedAt ?? cachedUnit?.localUpdatedAt,
        pendingAction: unitFromState.pendingAction ?? cachedUnit?.pendingAction,
        softLeases: unitFromState.softLeases ?? cachedUnit?.softLeases ?? [],
      }
    : cachedUnit;

  if (!unitToArchive) {
    throw new Error('Storage unit is not available in the offline cache');
  }

  const now = new Date();
  const nowIso = now.toISOString();

  if (unitToArchive.pendingAction === 'create' || id.startsWith('tempId-')) {
    const archivedPendingCreate = (yield storageUnitCache.setOne(
      {
        ...unitToArchive,
        softLeases: unitToArchive.softLeases ?? [],
        archived: true,
        updatedAt: now,
        isSynced: false,
        pendingAction: 'archive',
        pendingActionBeforeArchive: null,
        localUpdatedAt: nowIso,
      },
      {
        markSynced: false,
        pendingAction: 'archive',
        localUpdatedAt: nowIso,
      },
    )) as StorageUnit;
    yield storageUnitCache.updateUnitInLists(archivedPendingCreate);
    yield put(addMessage(fileArchivedMessage(id)));
    yield put(archiveStorageUnitSuccess(id));
    return;
  }

  const archivedUnit: StorageUnit = {
    ...unitToArchive,
    softLeases: unitToArchive.softLeases ?? [],
    archived: true,
    updatedAt: now,
    isSynced: false,
    pendingAction: 'archive',
    pendingActionBeforeArchive:
      unitToArchive.pendingAction === 'update' ? 'update' : null,
    localUpdatedAt: nowIso,
  };

  yield storageUnitCache.setOne(archivedUnit, {
    markSynced: false,
    pendingAction: 'archive',
    localUpdatedAt: nowIso,
  });
  yield storageUnitCache.updateUnitInLists(archivedUnit);
  yield put(addMessage(fileArchivedMessage(id)));
  yield put(archiveStorageUnitSuccess(id));
}

export function* archiveStorageUnit(
  action: ArchiveStorageUnitRequestActionInterface,
) {
  const { id } = action.payload;
  const storageUnitsState: StorageUnitsReducerInterface = yield select(
    (state: RootState) => state.storageUnitsState,
  );
  const oldUnit = findStorageUnitInState(storageUnitsState, id);

  try {
    yield put(setExplorerLoading(true));

    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline || !keycloak.token) {
      yield* archiveStorageUnitOffline(id, oldUnit);
      return;
    }

    const { parsedBody }: { parsedBody: StorageUnit } = yield withRetry(() =>
      archive(id),
    );
    const archivedUnit = parsedBody
      ? mergeSyncedStorageUnit(parsedBody, oldUnit ?? parsedBody)
      : oldUnit;
    if (archivedUnit) {
      yield storageUnitCache.setOne({
        ...archivedUnit,
        archived: true,
      });
    } else {
      yield storageUnitCache.clearOne(id);
    }
    yield storageUnitCache.clearList();
    yield put(addMessage(fileArchivedMessage(id)));
    yield put(archiveStorageUnitSuccess(id));
  } catch (error) {
    if (
      error instanceof NetworkError ||
      error instanceof TimeoutError ||
      error instanceof AuthenticationError
    ) {
      if (!(error instanceof AuthenticationError))
        yield call(requestConnectivityCheck);
      try {
        yield* archiveStorageUnitOffline(id, oldUnit);
      } catch (offlineError) {
        Sentry.captureException(offlineError, {
          tags: { saga: 'archiveStorageUnit', fallback: 'offline' },
          extra: action.payload,
        });
        yield put(addMessage(errorMessage('Failed to move file to bin.')));
      }
      return;
    }

    Sentry.captureException(error, {
      tags: { saga: 'archiveStorageUnit' },
      extra: action.payload,
    });
    yield put(addMessage(errorMessage('Failed to move file to bin.')));
  } finally {
    yield put(setExplorerLoading(false));
  }
}

export function* deleteAllArchivedStorageUnits() {
  try {
    yield put(setExplorerLoading(true));
    yield withRetry(() => permanentlyDeleteArchived());
    yield put(addMessage(allArchivedDeletedMessage()));
    yield put(deleteAllArchviedSuccess());
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'deleteAllArchivedStorageUnits' },
    });
    yield put(addMessage(errorMessage('Failed to empty the bin.')));
  } finally {
    yield put(setExplorerLoading(false));
  }
}

export function* restoreStorageUnit(
  action: RestoreStorageUnitRequestActionInterface,
) {
  const { id } = action.payload;
  try {
    yield put(setExplorerLoading(true));
    const isOnline: boolean = yield select(getIsOnline);

    if (!isOnline || !keycloak.token) {
      yield* restoreStorageUnitOffline(id);
      return;
    }

    yield withRetry(() => restore(id));
    const { parsedBody }: { parsedBody: StorageUnit } = yield withRetry(() =>
      getOne(id),
    );
    yield storageUnitCache.setOne({
      ...parsedBody,
      archived: false,
    });
    yield storageUnitCache.clearList();
    yield put(addMessage(fileRestoredMessage(parsedBody)));
    yield put(restoreStorageUnitSuccess(parsedBody));
  } catch (error) {
    if (
      error instanceof NetworkError ||
      error instanceof TimeoutError ||
      error instanceof AuthenticationError
    ) {
      if (!(error instanceof AuthenticationError))
        yield call(requestConnectivityCheck);
      try {
        yield* restoreStorageUnitOffline(id);
        return;
      } catch (offlineError) {
        Sentry.captureException(offlineError, {
          tags: { saga: 'restoreStorageUnit', fallback: 'offline' },
          extra: action.payload,
        });
      }
    }

    Sentry.captureException(error, {
      tags: { saga: 'restoreStorageUnit' },
      extra: action.payload,
    });
    yield put(addMessage(errorMessage('Failed to restore file.')));
  } finally {
    yield put(setExplorerLoading(false));
  }
}

function* restoreStorageUnitOffline(
  id: string,
): Generator<unknown, void, unknown> {
  const cachedUnit = (yield call(
    [storageUnitCache, storageUnitCache.getOne],
    id,
  )) as StorageUnit | null;

  if (!cachedUnit) {
    throw new Error('Storage unit is not available in the offline cache');
  }

  if (cachedUnit.pendingAction !== 'archive') {
    throw new Error('Storage unit does not have a pending local archive');
  }

  const nowIso = new Date().toISOString();
  const restoreAsPendingCreate = id.startsWith('tempId-');
  const restoredPendingAction = restoreAsPendingCreate
    ? 'create'
    : cachedUnit.pendingActionBeforeArchive === 'update'
      ? 'update'
      : null;
  const restoredLocalUpdatedAt = restoredPendingAction
    ? (cachedUnit.localUpdatedAt ?? nowIso)
    : null;
  const restoredUnit: StorageUnit = {
    ...cachedUnit,
    softLeases: cachedUnit.softLeases ?? [],
    archived: false,
    isSynced: !restoredPendingAction,
    pendingAction: restoredPendingAction,
    pendingActionBeforeArchive: null,
    localUpdatedAt: restoredLocalUpdatedAt,
  };

  const normalized = (
    restoredPendingAction
      ? yield storageUnitCache.setOne(restoredUnit, {
          markSynced: false,
          pendingAction: restoredPendingAction,
          localUpdatedAt: restoredLocalUpdatedAt ?? nowIso,
        })
      : yield storageUnitCache.setOne(restoredUnit)
  ) as StorageUnit;

  yield storageUnitCache.updateUnitInLists(normalized);
  yield put(addMessage(fileRestoredMessage(normalized)));
  yield put(restoreStorageUnitSuccess(normalized));
}
